<template>
  <div class="cart-item pb-0 flex flex-row space-x-0 lg:space-x-8 gap-2 sm:gap-0">
    <div class="flex-initial pr-2 w-1/3 lg:min-w-max">
      <img :src="img" alt="" class="product__image" width="100px" height="100px" />
    </div>
    <div class="flex-1 w-1/2 lg:w-auto">
      <div class="cart-item__description mb-3">
        <span class="break-all">{{ data.name }}</span>
      </div>
      <div class="text-xs text-gray-150" v-show="data.sku_item_parent_name_1">
        <i18n path="common.label.formatSKU">
          <template #sku_name>
            <span class="break-all">{{ data.sku_item_parent_name_1 }}</span>
          </template>
          <template #sku_value>
            <span class="ml-2 break-all">{{ data.sku_set_item_name1 }}</span>
          </template>
        </i18n>
      </div>
      <div class="text-xs mt-1 text-gray-150" v-show="data.sku_item_parent_name_2">
        <i18n path="common.label.formatSKU">
          <template #sku_name>
            <span class="break-all">{{ data.sku_item_parent_name_2 }}</span>
          </template>
          <template #sku_value>
            <span class="ml-2 break-all">{{ data.sku_set_item_name2 }}</span>
          </template>
        </i18n>
      </div>
      <div class="cart-item__wrap items-start">
        <div class="cart-item__amount flex" :class="[disabledItem ? '' : 'pt-2']">
          <i18n path="common.cart.formatLabel" class="flex items-center">
            <template #label>
              <span>{{ $t('common.cart.unitPrice') }}</span>
            </template>
            <template #tax>
              <span class="tax-font">{{ $t('common.cart.withTax') }}</span>
            </template>
          </i18n>
          <span>{{ $t('cartList.item.separatorColon') }}</span>
          <i18n path="common.cart.formatPrice">
            <template #price>
              <span>{{ data.price.toLocaleString() }}</span>
            </template>
            <template #unit>
              <span class="text-xxs">{{ $t('common.cart.yen') }}</span>
            </template>
          </i18n>
        </div>
        <div v-if="!disabledItem" class="cart-item__quantity flex flex-col items-end">
          <el-form-item
            class="ec-015"
            :prop="'subscription_order_item.' + index + '.quantity'"
            :rules="rules"
            :class="{ 'cursor-not-allowed': disabledItem }"
          >
            <base-input-number
              :value="data.quantity"
              :max-value="data.sale_limit"
              :min-value="1"
              :disabled="disabledItem"
              @input="updateQuantity"
            ></base-input-number>
          </el-form-item>
        </div>
      </div>
      <div v-if="disabledItem" class="text-sm lg:text-base mt-2 mb-4">
        <span>{{ $t('cartList.item.quantity') }}</span>
        <span>{{ $t('cartList.item.separatorColon') }}</span>
        <span>{{ data.quantity }}</span>
      </div>
      <div class="cart-item__total flex">
        <i18n path="common.cart.formatLabel" class="flex items-center">
          <template #label>
            <span>{{ $t('common.cart.subTotal') }}</span>
          </template>
          <template #tax>
            <span class="tax-font">{{ $t('common.cart.withTax') }}</span>
          </template>
        </i18n>
        <span>{{ $t('cartList.item.separatorColon') }}</span>
        <i18n path="common.cart.formatPrice">
          <template #price>
            <span>{{ data.subtotal.toLocaleString() }}</span>
          </template>
          <template #unit>
            <span class="text-xxs">{{ $t('common.cart.yen') }}</span>
          </template>
        </i18n>
      </div>
      <div v-if="data.discount_amount_total !== 0" class="cart-item__discount mt-1">
        <i18n path="common.cart.formatLabel">
          <template #label>
            <span class="font-light">{{ $t('common.label.priceDiscount') }}</span>
          </template>
        </i18n>
        <span class="font-light">{{ $t('cartList.item.separatorColon') }}</span>
        <i18n path="common.cart.formatPrice" class="text-red">
          <template #price>
            <span>{{ data.discount_amount_total.toLocaleString() }}</span>
          </template>
          <template #unit>
            <span class="text-xxs">{{ $t('common.cart.yen') }}</span>
          </template>
        </i18n>
      </div>
      <div class="cart-item__wrap">
        <el-button
          class="ml-auto text-gray-150 py-0 lg:text-base mb-5 lg:mb-6 font-normal"
          type="text"
          :disabled="disableDeleteBtn"
          @click="removeItem"
        >
          {{ $t('common.action.delete') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInputNumber from '@/components/BaseInputNumber.vue'

export default {
  components: { BaseInputNumber },
  props: {
    data: { type: Object, required: true, default: () => {} },
    index: {
      type: Number,
      default: null,
    },
    disabledItem: { type: Boolean, default: () => false },
    disableDeleteBtn: { type: Boolean, default: () => false },
  },
  data() {
    const mappingMaxValue = (rule, value, callback) => {
      if (this.data.sale_limit && value > this.data.sale_limit) {
        callback(new Error(this.$t('common.cart.maxInputNumber', { val: this.data.sale_limit })))
      }

      callback()
    }
    return {
      rules: [
        {
          validator: mappingMaxValue,
          trigger: ['change', 'blur'],
        },
      ],
    }
  },
  computed: {
    img() {
      return this.data.product_image ? this.data.product_image : require('@/assets/images/gray-image.svg')
    },
  },
  methods: {
    removeItem() {
      this.$emit('remove', this.data)
    },
    updateQuantity(payload) {
      if (this.data.sale_limit && payload > this.data.sale_limit) {
        this.handleError({
          message: this.$t('common.cart.saleLimitToast', { name: this.data.name, saleLimit: this.data.sale_limit }),
        })
      }
      this.$emit('update-quantity', {
        product_sku_id: this.data.product_sku_id,
        quantity: payload,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/cart-list-item.scss';
</style>
